import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "../common/Modal";
import reverseSyncExecutionService from "../../services/legacy/reverseSyncExecutionService";

const panelStyle = {
  marginBottom: "16px",
};

const bodyStyle = {
  display: "flex",
  flexDirection: "column",
  gap: "8px",
};

const rowStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: "10px",
  alignItems: "center",
};

const mutedStyle = {
  fontSize: "12px",
  opacity: 0.75,
};

const statusPillStyle = (status) => {
  if (status === "FUNCTIONAL_FAILED" || status === "TECHNICAL_FAILED") {
    return { background: "#fce8e6", color: "#9c1c14", border: "1px solid #f3b3ad" };
  }
  if (status === "SUCCEEDED") {
    return { background: "#e6f4ea", color: "#1e6b35", border: "1px solid #b7dfc4" };
  }
  return { background: "#f1f3f4", color: "#37474f", border: "1px solid #d0d7de" };
};

const pillBaseStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 600,
};

const toEpoch = (value) => {
  if (!value) {
    return 0;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const chooseNewest = (current, incoming) => {
  if (!incoming) {
    return current;
  }
  if (!current) {
    return incoming;
  }

  const currentTs = Math.max(toEpoch(current.processedAt), toEpoch(current.updatedAt), toEpoch(current.createdAt));
  const incomingTs = Math.max(toEpoch(incoming.processedAt), toEpoch(incoming.updatedAt), toEpoch(incoming.createdAt));
  return incomingTs >= currentTs ? incoming : current;
};

const toUiStatus = (entry) => {
  if (!entry) {
    return "PENDING";
  }
  if (entry.technicalStatus === "FAILED") {
    return "TECHNICAL_FAILED";
  }
  if (entry.functionalStatus === "FAILED") {
    return "FUNCTIONAL_FAILED";
  }
  if (entry.technicalStatus === "SUCCEEDED") {
    return "SUCCEEDED";
  }
  return "PENDING";
};

const prettyDateTime = (value) => {
  if (!value) {
    return "-";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const normalizeBusinessErrorMessage = (message) => {
  if (!message || typeof message !== "string") {
    return message;
  }

  return message
    .replace(/^Business Connector Proxy failed:\s*/i, "")
    // AXBusinessConnectorProxyService prefixes this internal sentinel when
    // AX AOS logon itself fails (as opposed to a successful logon followed
    // by AX rejecting the call), so the .NET 8 worker's BCProxy client can
    // classify it into a dedicated retry window instead of the generic one.
    // It must never reach this UI as raw text - strip it here defensively
    // even though BCProxy.cs also strips it server-side, since an
    // out-of-date worker build (not yet rebuilt with that fix) still
    // forwards the marker verbatim through the generic failure path.
    .replace(/^AX_AOS_UNAVAILABLE::\s*/i, "")
    .trim();
};

const ExecutionRow = ({ label, entry }) => {
  const uiStatus = toUiStatus(entry);
  const { t } = useTranslation();
  const functionalMessage = normalizeBusinessErrorMessage(entry?.functionalError);
  const technicalMessage = normalizeBusinessErrorMessage(entry?.technicalError);
  const detailMessage = functionalMessage || technicalMessage;

  return (
    <div style={bodyStyle}>
      <div style={rowStyle}>
        <strong>{label}</strong>
        <span style={{ ...pillBaseStyle, ...statusPillStyle(uiStatus) }}>{t(`reverseSync.status.${uiStatus}`)}</span>
        <span style={mutedStyle}>{t("reverseSync.operationLabel")}: {entry?.operation || "-"}</span>
        <span style={mutedStyle}>{t("reverseSync.processedLabel")}: {prettyDateTime(entry?.processedAt)}</span>
      </div>
      {detailMessage && <div style={{ color: "#8b1e1e", fontSize: "12px" }}>{detailMessage}</div>}
    </div>
  );
};

export default function ReverseSyncStatusPanel({
  dataAreaId,
  salesOrderId,
  onExecutionEvent,
  extraStreamEntities,
  onExtraEntityMessage,
}) {
  const { t } = useTranslation();
  const [salesTableExecution, setSalesTableExecution] = useState(null);
  const [salesLineExecution, setSalesLineExecution] = useState(null);
  const [itemCreationExecution, setItemCreationExecution] = useState(null);
  const [loading, setLoading] = useState(false);
  const [streamError, setStreamError] = useState(null);
  const [errorPopup, setErrorPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    operation: "",
    processedAt: "",
  });
  const shownFailureSignaturesRef = useRef(new Set());

  // Entities not shown as a row in this panel, but whose live updates the
  // caller still wants to piggyback on this same connection (see
  // `stream` context below / onExtraEntityMessage).
  const normalizedExtraEntities = useMemo(
    () => (Array.isArray(extraStreamEntities) ? extraStreamEntities.filter(Boolean) : []),
    [extraStreamEntities],
  );

  const contexts = useMemo(() => {
    if (!dataAreaId || !salesOrderId) {
      return null;
    }
    const orderBusinessKey = `${dataAreaId}:${salesOrderId}`;
    return {
      // One-shot initial-state lookups: cheap, short-lived REST calls, kept
      // separate per entity for clarity - they don't hold a connection open.
      salesTable: { entity: "SalesTable", businessKey: orderBusinessKey, dataAreaId },
      salesLine: { entity: "SalesLine", businessKeyPrefix: `${orderBusinessKey}:`, dataAreaId },
      itemCreation: { entity: "InventItemCreation", businessKeyPrefix: `${orderBusinessKey}:ITEM:`, dataAreaId },
      // Single long-lived SSE connection watching all entities for this order at
      // once (SalesTable/SalesLine/InventItemCreation + any extra entities the
      // caller asked for). Consolidating into one connection avoids exhausting
      // the browser's per-origin connection cap when several sales-order tabs
      // are open at the same time.
      stream: {
        entities: ["SalesTable", "SalesLine", "InventItemCreation", ...normalizedExtraEntities],
        orderScopeKey: orderBusinessKey,
        dataAreaId,
      },
    };
  }, [dataAreaId, salesOrderId, normalizedExtraEntities]);

  const maybeOpenFailurePopup = useCallback((entry, label) => {
    const uiStatus = toUiStatus(entry);
    if (uiStatus !== "FUNCTIONAL_FAILED" && uiStatus !== "TECHNICAL_FAILED") {
      return;
    }

    const message = normalizeBusinessErrorMessage(entry?.functionalError || entry?.technicalError);
    if (!message) {
      return;
    }

    const signature = [
      entry?.messageId || "",
      uiStatus,
      entry?.operation || "",
      message,
    ].join("|");

    if (shownFailureSignaturesRef.current.has(signature)) {
      return;
    }

    shownFailureSignaturesRef.current.add(signature);
    setErrorPopup({
      isOpen: true,
      title: t("reverseSync.popup.title", { scope: label }),
      message,
      operation: entry?.operation || "-",
      processedAt: prettyDateTime(entry?.processedAt),
    });
  }, [t]);

  useEffect(() => {
    if (!contexts) {
      setSalesTableExecution(null);
      setSalesLineExecution(null);
      setItemCreationExecution(null);
      setLoading(false);
      setStreamError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setStreamError(null);

    const loadInitial = async () => {
      try {
        const [tableEntry, lineEntry, itemEntry] = await Promise.all([
          reverseSyncExecutionService.getLatestByContext(contexts.salesTable),
          reverseSyncExecutionService.getLatestByContext(contexts.salesLine),
          reverseSyncExecutionService.getLatestByContext(contexts.itemCreation),
        ]);
        if (!cancelled) {
          setSalesTableExecution(tableEntry);
          setSalesLineExecution(lineEntry);
          setItemCreationExecution(itemEntry);
          maybeOpenFailurePopup(tableEntry, t("reverseSync.scope.salesOrder"));
          maybeOpenFailurePopup(lineEntry, t("reverseSync.scope.salesLines"));
          maybeOpenFailurePopup(itemEntry, t("reverseSync.scope.itemCreation"));
        }
      } catch (error) {
        if (!cancelled) {
          setStreamError(error?.message || t("reverseSync.errors.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadInitial();

    // Single connection for every entity relevant to this order. Routing is
    // done client-side by inspecting `entry.entity`, preserving the exact
    // per-entity behavior the 3 (now 4, counting the caller's extra entity)
    // previously-separate connections had.
    const unsubscribeStream = reverseSyncExecutionService.subscribeByContext(contexts.stream, {
      onMessage: (entry, eventName) => {
        const entity = entry?.entity;

        if (entity === "SalesTable") {
          if (eventName === "ax4-idax-sync-applied") {
            if (onExecutionEvent) {
              onExecutionEvent({ type: "AX4_TO_IDAX_SYNC_APPLIED", eventName, payload: entry });
            }
            return;
          }
          if (onExecutionEvent) {
            onExecutionEvent({ type: "REVERSE_SYNC_EXECUTION", eventName, payload: entry });
          }
          setSalesTableExecution((current) => {
            const next = chooseNewest(current, entry);
            maybeOpenFailurePopup(next, t("reverseSync.scope.salesOrder"));
            return next;
          });
          return;
        }

        if (entity === "SalesLine") {
          if (eventName === "ax4-idax-sync-applied") {
            if (onExecutionEvent) {
              onExecutionEvent({ type: "AX4_TO_IDAX_SYNC_APPLIED", eventName, payload: entry });
            }
            return;
          }
          if (onExecutionEvent) {
            onExecutionEvent({ type: "REVERSE_SYNC_EXECUTION", eventName, payload: entry });
          }
          setSalesLineExecution((current) => {
            const next = chooseNewest(current, entry);
            maybeOpenFailurePopup(next, t("reverseSync.scope.salesLines"));
            return next;
          });
          return;
        }

        if (entity === "InventItemCreation") {
          if (onExecutionEvent) {
            onExecutionEvent({ type: "REVERSE_SYNC_EXECUTION", eventName, payload: entry });
          }
          setItemCreationExecution((current) => {
            const next = chooseNewest(current, entry);
            maybeOpenFailurePopup(next, t("reverseSync.scope.itemCreation"));
            return next;
          });
          return;
        }

        if (entity && normalizedExtraEntities.includes(entity) && onExtraEntityMessage) {
          onExtraEntityMessage(entity, entry, eventName);
        }
      },
      onError: (error) => setStreamError(error?.message || t("reverseSync.errors.streamUnavailable")),
    });

    return () => {
      cancelled = true;
      unsubscribeStream();
    };
  }, [contexts, maybeOpenFailurePopup, normalizedExtraEntities, onExecutionEvent, onExtraEntityMessage, t]);

  if (!salesOrderId || !dataAreaId) {
    return null;
  }

  return (
    <>
      <section className="panel" style={panelStyle}>
        <header className="panel-heading">
          <h2 className="panel-title">{t("reverseSync.title")}</h2>
        </header>
        <div className="panel-body" style={bodyStyle}>
          {loading && <div style={mutedStyle}>{t("reverseSync.loading")}</div>}
          <ExecutionRow label={t("reverseSync.scope.salesOrder")} entry={salesTableExecution} />
          <ExecutionRow label={t("reverseSync.scope.salesLines")} entry={salesLineExecution} />
          <ExecutionRow label={t("reverseSync.scope.itemCreation")} entry={itemCreationExecution} />
          {streamError && <div style={{ color: "#8b1e1e", fontSize: "12px" }}>{streamError}</div>}
        </div>
      </section>

      <Modal
        isOpen={errorPopup.isOpen}
        onClose={() => setErrorPopup((current) => ({ ...current, isOpen: false }))}
        title={errorPopup.title || t("reverseSync.popup.fallbackTitle")}
        icon={faTriangleExclamation}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          <div style={{ color: "#8b1e1e", whiteSpace: "pre-wrap" }}>{errorPopup.message}</div>
          <div style={mutedStyle}>{t("reverseSync.operationLabel")}: {errorPopup.operation || "-"}</div>
          <div style={mutedStyle}>{t("reverseSync.processedLabel")}: {errorPopup.processedAt || "-"}</div>
          <div className="modal-footer" style={{ padding: 0, marginTop: "8px" }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setErrorPopup((current) => ({ ...current, isOpen: false }))}
            >
              {t("common.cancel")}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
